import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, Phone, Facebook, Instagram, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '../ui/sheet';
import { companyInfo, navLinks } from '../../data/mock';
import { useEstimateModal } from '../EstimateModalContext';

const socialLinks = {
  facebook: "https://www.facebook.com/profile.php?id=61577688976534",
  instagram: "https://www.instagram.com/supaenergyhub"
};

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { openModal } = useEstimateModal();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (href) => {
    if (location.pathname === href) return true;
    // Treat the catalog/brand top-levels as path prefixes so deep PDP/brand
    // routes still highlight the right top-nav item.
    if (
      (href === '/products/batteries' || href === '/brands' || href === '/heat-pump' || href === '/packages') &&
      location.pathname.startsWith(href)
    ) {
      return true;
    }
    // GoodWe package pages live at top-level (/goodwe-battery-packages, /goodwe-g20-...)
    if (href === '/goodwe-battery-packages' && location.pathname.startsWith('/goodwe-')) {
      return true;
    }
    // Deye package pages live at top-level (/deye-battery-packages, /deye-...kwh-battery-package)
    if (href === '/deye-battery-packages' && location.pathname.startsWith('/deye-')) {
      return true;
    }
    // /heat-pump should also "own" /products/heat-pumps and its PDP routes
    if (href === '/heat-pump' && location.pathname.startsWith('/products/heat-pumps')) {
      return true;
    }
    return false;
  };
  const isGroupActive = (link) =>
    isActive(link.href) ||
    (link.children && link.children.some((c) => isActive(c.href)));

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'mx-2 md:mx-4 mt-2 md:mt-3 rounded-full bg-white/80 backdrop-blur-xl border border-black/5 shadow-lg'
          : 'mx-2 md:mx-4 mt-2 md:mt-3 rounded-full bg-white/60 backdrop-blur-md border border-black/5'
      }`}
    >
      <div className="flex items-center justify-between h-16 md:h-[72px] px-3 md:px-6">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2">
          <img 
            src={companyInfo.logo} 
            alt={companyInfo.brandName} 
            className="h-10 md:h-12 w-auto"
          />
        </Link>
          
        {/* Mobile/Tablet: Social & Call Icons - Centered */}
        <div className="flex lg:hidden items-center gap-2">
          <a
            href={socialLinks.facebook}
            target="_blank"
            rel="noopener noreferrer"
            className="w-9 h-9 rounded-full bg-[#3D5A47]/10 flex items-center justify-center"
            aria-label="Facebook"
          >
            <Facebook className="w-4 h-4 text-[#3D5A47]" />
          </a>
          <a
            href={socialLinks.instagram}
            target="_blank"
            rel="noopener noreferrer"
            className="w-9 h-9 rounded-full bg-[#3D5A47]/10 flex items-center justify-center"
            aria-label="Instagram"
          >
            <Instagram className="w-4 h-4 text-[#3D5A47]" />
          </a>
          <a
            href={`tel:${companyInfo.phone}`}
            className="w-9 h-9 rounded-full bg-[#3D5A47]/10 flex items-center justify-center"
            aria-label="Call us"
          >
            <Phone className="w-4 h-4 text-[#3D5A47]" />
          </a>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-1 min-w-0">
          {navLinks.map((link) =>
            link.children ? (
              <div key={link.href} className="relative group">
                <button
                  type="button"
                  className={`nav-link inline-flex items-center gap-1 ${
                    isGroupActive(link)
                      ? 'bg-[#3D5A47]/10 text-[#3D5A47]'
                      : ''
                  }`}
                  data-testid={`nav-${link.label.toLowerCase()}-trigger`}
                >
                  {link.label}
                  <ChevronDown className="w-3.5 h-3.5 transition-transform group-hover:rotate-180" />
                </button>
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50">
                  <div className="bg-white rounded-2xl shadow-xl border border-gray-100 py-2 w-56">
                    {link.children.map((child) => (
                      <Link
                        key={child.href}
                        to={child.href}
                        className={`block px-4 py-2.5 text-sm font-medium transition-colors ${
                          isActive(child.href)
                            ? 'bg-[#B5D43B]/15 text-[#3D5A47]'
                            : 'text-[#1D1D1B] hover:bg-[#3D5A47]/5 hover:text-[#3D5A47]'
                        }`}
                        data-testid={`nav-child-${child.href.replace(/\//g, '')}`}
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <Link
                key={link.href}
                to={link.href}
                className={`nav-link ${
                  isActive(link.href)
                    ? 'bg-[#3D5A47]/10 text-[#3D5A47]'
                    : ''
                }`}
              >
                {link.label}
              </Link>
            )
          )}
        </nav>

        {/* Desktop CTA */}
        <div className="hidden lg:flex items-center gap-3 shrink-0">
          <div className="hidden xl:flex items-center gap-1 mr-2">
            <a
              href={socialLinks.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 rounded-full hover:bg-[#B5D43B]/15 flex items-center justify-center transition-colors"
              aria-label="Facebook"
            >
              <Facebook className="w-5 h-5 text-[#3D5A47]" />
            </a>
            <a
              href={socialLinks.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 rounded-full hover:bg-[#B5D43B]/15 flex items-center justify-center transition-colors"
              aria-label="Instagram"
            >
              <Instagram className="w-5 h-5 text-[#3D5A47]" />
            </a>
          </div>
          <a
            href={`tel:${companyInfo.phone}`}
            className="flex items-center gap-2 text-sm font-medium text-[#3D5A47] hover:text-[#B5D43B] transition-colors whitespace-nowrap shrink-0"
          >
            <Phone className="w-4 h-4 shrink-0" />
            <span className="hidden xl:inline">{companyInfo.phone}</span>
          </a>
          <Button 
            onClick={openModal}
            className="btn-primary text-sm px-4 xl:px-6 py-3 h-auto flex flex-col items-center gap-0 whitespace-nowrap shrink-0"
          >
            <span>Get Free Estimate</span>
            <span className="text-[10px] opacity-80 font-normal">Takes only 30 secs</span>
          </Button>
        </div>

        {/* Mobile Menu */}
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetTrigger asChild className="lg:hidden">
            <Button variant="ghost" size="icon" className="h-10 w-10">
              <Menu className="h-6 w-6" />
              <span className="sr-only">Toggle menu</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[280px] sm:w-[320px]">
            <SheetTitle className="text-left">
              <img 
                src={companyInfo.logo} 
                alt={companyInfo.brandName} 
                className="h-10 w-auto"
              />
            </SheetTitle>
            <nav className="flex flex-col gap-2 mt-8">
              {navLinks.map((link) =>
                link.children ? (
                  <div key={link.href}>
                    <div className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-[#3D5A47]/70">
                      {link.label}
                    </div>
                    {link.children.map((child) => (
                      <Link
                        key={child.href}
                        to={child.href}
                        onClick={() => setIsOpen(false)}
                        className={`block pl-7 pr-4 py-2.5 rounded-lg text-base font-medium transition-colors ${
                          isActive(child.href)
                            ? 'bg-[#B5D43B]/20 text-[#3D5A47]'
                            : 'text-[#1D1D1B] hover:bg-[#3D5A47]/5'
                        }`}
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setIsOpen(false)}
                    className={`px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                      isActive(link.href)
                        ? 'bg-[#B5D43B]/20 text-[#3D5A47]'
                        : 'text-[#1D1D1B] hover:bg-[#3D5A47]/5'
                    }`}
                  >
                    {link.label}
                  </Link>
                )
              )}
            </nav>
            <div className="mt-8 pt-8 border-t">
              <a
                href={`tel:${companyInfo.phone}`}
                className="flex items-center gap-3 px-4 py-3 text-[#3D5A47] hover:text-[#B5D43B] transition-colors"
              >
                <Phone className="w-5 h-5" />
                <span className="font-medium">{companyInfo.phone}</span>
              </a>
              <Button 
                onClick={() => { setIsOpen(false); openModal(); }}
                className="btn-primary w-full mt-4"
              >
                Get Free Estimate
              </Button>
              {/* Social Media Links */}
              <div className="flex items-center justify-center gap-4 mt-6">
                <a
                  href={socialLinks.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-[#3D5A47]/10 hover:bg-[#B5D43B] flex items-center justify-center transition-all group"
                  aria-label="Facebook"
                >
                  <Facebook className="w-5 h-5 text-[#3D5A47] group-hover:text-white" />
                </a>
                <a
                  href={socialLinks.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-[#3D5A47]/10 hover:bg-[#B5D43B] flex items-center justify-center transition-all group"
                  aria-label="Instagram"
                >
                  <Instagram className="w-5 h-5 text-[#3D5A47] group-hover:text-white" />
                </a>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};

export default Header;
